import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import type { ApiError } from '@/shared/lib/problem-details';
import { getCancellationRequests } from '../api/orders.api';
import type { CancellationRequestListItem, CancellationRequestListResponse } from '../types/orders.types';
import { recordServerTime, serverClockNow } from '../utils/server-clock';
import { ordersKeys } from './useOrders';

/* ==========================================================================
   Lista de solicitações de cancelamento (change orders-customer-cancellation,
   D21 e D22): o chip da tela de pedidos e o alerta global leem daqui.

   Releitura: pelos eventos `order.cancellation_requested` e
   `order.cancellation_request_resolved` e na reconexão (a chave fica debaixo
   da raiz dos pedidos, que o tempo real invalida), e a cada 30 segundos
   enquanto a lista não estiver vazia: a vencida sai dela 2 horas depois do
   prazo sem evento nenhum. Com uma aguardando, a próxima leitura também vem
   logo depois do prazo dela, para a vencida aparecer com o estado do servidor.
   ========================================================================== */

export const CANCELLATION_REQUESTS_REFETCH_MS = 30_000;

/**
 * Depois do prazo, a margem para a varredura do servidor (5 s) já ter vencido
 * a solicitação e, se for o caso, passado a conversa para a equipe: a vencida
 * escalada sai da lista, e o alerta de atendimento assume sozinho.
 */
export const AFTER_DEADLINE_MS = 6_000;

/** O intervalo da próxima releitura, ou `false` com a lista vazia. */
export function cancellationRequestsRefetchInterval(
  data: CancellationRequestListResponse | undefined,
  now = serverClockNow(),
): number | false {
  if (!data || data.items.length === 0) return false;
  const deadlines = data.items
    .filter((item) => item.state === 'pending')
    .map((item) => Date.parse(item.deadline) - now)
    .filter((ms) => Number.isFinite(ms) && ms >= 0);
  if (deadlines.length === 0) return CANCELLATION_REQUESTS_REFETCH_MS;
  return Math.min(CANCELLATION_REQUESTS_REFETCH_MS, Math.min(...deadlines) + AFTER_DEADLINE_MS);
}

export interface UseCancellationRequestsOptions {
  /** Falso: não pergunta (a Cozinha no chip, o bloco sem solicitação aguardando). */
  enabled?: boolean;
}

export function useCancellationRequests({ enabled = true }: UseCancellationRequestsOptions = {}) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<CancellationRequestListResponse, ApiError>({
    queryKey: ordersKeys.cancellationRequests(tenantId),
    queryFn: async () => {
      const data = await getCancellationRequests();
      recordServerTime(data.serverTime);
      return data;
    },
    enabled: enabled && !!tenantId,
    refetchInterval: (query) => cancellationRequestsRefetchInterval(query.state.data),
    // O 403 de um token antigo não é falha de tela: o chip e o alerta somem.
    retry: false,
  });
}

/**
 * O estado efetivo do item no relógio do servidor: a aguardando com o prazo
 * passado já conta como vencida, sem esperar a próxima leitura.
 */
export function effectiveListState(item: CancellationRequestListItem, now: number): 'pending' | 'expired' {
  if (item.state === 'expired') return 'expired';
  const deadline = Date.parse(item.deadline);
  return Number.isFinite(deadline) && deadline <= now ? 'expired' : 'pending';
}
